import "./fund_card.scss";
import { Text } from "@/elements/text/text";
import { Formatter } from "@/model/formatter";
import { i18n } from "@/model/i18n";
import { CodeSnippet } from "@/components/code_snippet/code_snippet";
import { FundTypeName } from "@/model/db";
import { ReactElement } from "react";
import classnames from "classnames";

interface Props {
  active?: boolean;
  isin: string | null;
  link?: string;
  nav: number | null;
  riskClass: number;
  stand: Date | null;
  title: string;
  type: FundTypeName;
  volumen: number;
  equityQuotaPerc?: number | null;
  sfdrClassification?: string | null;
  wkn?: string | null;
}

export function FundCard({
  active,
  isin,
  nav,
  riskClass,
  stand,
  title,
  type,
  volumen,
  equityQuotaPerc,
  sfdrClassification,
  wkn,
}: Props): ReactElement {
  const formatter = new Formatter({ locale: "de" });

  const extraFields = [
    equityQuotaPerc !== undefined,
    sfdrClassification !== undefined,
    wkn !== undefined,
  ].some(Boolean);

  return (
    <section
      aria-label="fund-card"
      className={classnames("fund-card", {
        "fund-card--active": active,
        "fund-card--with-extra-fields": extraFields,
      })}
    >
      <div className="fund-card__content">
        <header className="fund-card__header">
          <Text
            htmlElement="p"
            type="micro"
            color="darker-pairings-primary"
            uppercase
          >
            {type}
          </Text>
          <Text
            htmlElement="h4"
            type={extraFields ? "h2-xbold" : "h3-xbold"}
            color="accent-tinted"
          >
            {title}
          </Text>
        </header>
        <ul className="fund-card__main">
          <li className="fund-card__main-item fund-card__main-item--risk">
            <Text
              htmlElement="p"
              type="micro"
              color="neutral-on-surface-lightest"
              uppercase
            >
              {i18n.t("components.fund_card.risk_class")}
            </Text>
            <CodeSnippet small title={String(riskClass)} />
          </li>
          {equityQuotaPerc && (
            <li className="fund-card__main-item fund-card__main-item--equity-quota-perc">
              <Text
                htmlElement="p"
                type="micro"
                color="neutral-on-surface-lightest"
                uppercase
              >
                {i18n.t("components.fund_card.equity_quota_perc")}
              </Text>
              <CodeSnippet
                small
                title={formatter.roundPercentage(equityQuotaPerc)}
              />
            </li>
          )}
          {sfdrClassification && (
            <li className="fund-card__main-item fund-card__main-item--sfdr-classification">
              <Text
                htmlElement="p"
                type="micro-no-uppercase"
                color="neutral-on-surface-lightest"
              >
                {i18n.t("components.fund_card.sfdr_classification")}
              </Text>
              <CodeSnippet small title={sfdrClassification} />
            </li>
          )}
          <li className="fund-card__main-item fund-card__main-item--volume">
            <Text
              htmlElement="p"
              type="micro"
              color="neutral-on-surface-lightest"
              uppercase
            >
              {i18n.t("components.fund_card.volumen")}
            </Text>
            <Text
              htmlElement="p"
              type="number-snippet"
              color="primary-on-default"
            >
              {formatter.currency(volumen)}
            </Text>
          </li>

          <li className="fund-card__main-item fund-card__main-item--isin">
            <Text
              htmlElement="p"
              type="micro"
              color="neutral-on-surface-lightest"
              uppercase
            >
              {i18n.t("components.fund_card.isin")}
            </Text>
            <Text
              htmlElement="p"
              type="number-snippet"
              color="primary-on-default"
            >
              {isin}
            </Text>
          </li>
          <li className="fund-card__main-item fund-card__main-item--nav">
            <Text
              htmlElement="p"
              type="micro"
              color="neutral-on-surface-lightest"
              uppercase
            >
              {i18n.t("components.fund_card.nav")}
            </Text>
            <Text
              htmlElement="p"
              type="number-snippet"
              color="primary-on-default"
            >
              {formatter.currency(nav || 0)}
            </Text>
          </li>
          <li className="fund-card__main-item fund-card__main-item--stand">
            <Text
              htmlElement="p"
              type="micro"
              color="neutral-on-surface-lightest"
              uppercase
            >
              {i18n.t("components.fund_card.stand")}
            </Text>
            <Text
              htmlElement="p"
              type="number-snippet"
              color="primary-on-default"
            >
              {stand ? formatter.numeralDate(stand) : "-"}
            </Text>
          </li>
          {wkn && (
            <li className="fund-card__main-item fund-card__main-item--wkn">
              <Text
                htmlElement="p"
                type="micro"
                color="neutral-on-surface-lightest"
                uppercase
              >
                {i18n.t("components.fund_card.wkn")}
              </Text>
              <Text
                htmlElement="p"
                type="number-snippet"
                color="primary-on-default"
              >
                {wkn}
              </Text>
            </li>
          )}
        </ul>
      </div>
      {/* {link && (
        <footer className="fund-card__footer">
          <Link href={link}>
            <Button
              type="secondary"
              title={i18n.t("components.fund_card.know_more")}
            />
          </Link>
        </footer>
      )} */}
    </section>
  );
}
